import { useCallback, useEffect, useState } from 'react';

import shownActivated from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_activated_screen_shown';
import clickCancelConfirm from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_cancel_confirm_button_click';
import clickCancelDecline from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_cancel_decline_button_click';
import shownCancel from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_cancel_screen_shown';
import shownError from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_payment_failure_screen_shown';
import clickRetry from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_payment_try_again_button_click';
import clickRenew from '@hh.ru/analytics-js-events/build/xhh/applicant/hhpro/hhpro_renew_button_click';
import { translation, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import {
    Alert,
    BottomSheet,
    BottomSheetFooter,
    Button,
    Modal,
    Snackbar,
    Title,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { InfoCircleFilledSize24, ExclamationTriangleFilledSize24, EmojiSadFilledSize24 } from '@hh.ru/magritte-ui/icon';

import FormatMoney from 'src/components/FormatMoney';
import { CurrencyType } from 'src/components/FormatMoney/CurrencyType';
import HumanDate from 'src/components/HumanDate';
import { SubscriptionAction } from 'src/hooks/hhPro/usePayment';
import { FormState } from 'src/types/HhProFormState';
import { UserSubscription } from 'src/types/hhpro';

import styles from './styles.less';

const TrlKeys = {
    waitingTitle: 'hhpro.promo.state.waitingTitle',
    waitingDescription: 'hhpro.promo.state.waitingDescription',
    activatedTitle: 'hhpro.promo.state.activatedTitle',
    activatedDescription: 'hhpro.promo.state.activatedDescription',
    activatedButton: 'hhpro.promo.state.activatedButton',
    errorTitle: 'hhpro.promo.state.errorTitle',
    errorDescription: 'hhpro.promo.state.errorDescription',
    errorButton: 'hhpro.promo.state.errorButton',
    cancel: {
        title: 'hhpro.promo.state.cancel.title',
        description: 'hhpro.promo.state.cancel.description',
        apply: 'hhpro.promo.state.cancel.apply',
        cancel: 'hhpro.promo.state.cancel.cancel',
        complete: 'hhpro.promo.state.cancel.complete',
        renew: 'hhpro.promo.state.cancel.renew',
    },
    prepaymentTitle: 'hhpro.promo.state.prepayment.title',
    prepaymentDescription: 'hhpro.promo.state.prepayment.description',
    prepaymentButton: 'hhpro.promo.state.prepayment.button',
};

const ACTIVATED_SOURCE = 'hhpro_activated';
const CANCEL_SOURCE = 'hhpro_cancel';
const ERROR_SOURCE = 'hhpro_payment_failure';

const PaymentState: TranslatedComponent<{
    orderCode?: string;
    frameSrc?: string;
    formState: FormState;
    subscription?: UserSubscription;
    initPayment: () => void;
    initCancel: () => void;
    initRenew: () => void;
    lastAction?: SubscriptionAction;
    isFetching: boolean;
    setFormState: (formState: FormState) => void;
    replaceUrlRemoveState: () => void;
    price: number;
    currency: CurrencyType;
}> = ({
    subscription,
    orderCode,
    initPayment,
    frameSrc,
    formState,
    setFormState,
    initCancel,
    initRenew,
    lastAction,
    isFetching,
    trls,
    price,
    currency,
    replaceUrlRemoveState,
}) => {
    const breakpoint = useBreakpoint();
    const [showSnackbar, setShowSnackbar] = useState(false);

    const setIdle = useCallback(() => {
        replaceUrlRemoveState();
        setFormState(FormState.Idle);
    }, [replaceUrlRemoveState, setFormState]);

    const runLastAction = useCallback(() => {
        if (lastAction) {
            clickRetry({ lastAction });
        }
        switch (lastAction) {
            case SubscriptionAction.Payment:
                initPayment();
                break;
            case SubscriptionAction.Cancel:
                initCancel();
                break;
            case SubscriptionAction.Renew:
                initRenew();
                break;
            default:
                initPayment();
        }
    }, [initCancel, initPayment, initRenew, lastAction]);

    useEffect(() => {
        if (formState === FormState.Canceled) {
            setShowSnackbar(true);
        } else if ([FormState.FetchingRenew, FormState.Activated, FormState.Error].includes(formState)) {
            setShowSnackbar(false);
        }
    }, [formState]);

    const renderActivatedButton = useCallback(
        () => (
            <Button
                mode="primary"
                size={breakpoint.isMobile ? 'medium' : 'large'}
                stretched
                onClick={setIdle}
                data-qa="hhpro-activated-button"
            >
                {trls[TrlKeys.activatedButton]}
            </Button>
        ),
        [breakpoint.isMobile, setIdle, trls]
    );

    const renderActivatedContent = useCallback(
        () => (
            <>
                <div className={styles.activated} />
                <VSpacing default={24} />
                <Title Element="h6" size="medium" alignment="center" description={trls[TrlKeys.activatedDescription]}>
                    {trls[TrlKeys.activatedTitle]}
                </Title>
            </>
        ),
        [trls]
    );

    useEffect(() => {
        switch (formState) {
            case FormState.Activated:
                shownActivated({ hhtmSource: ACTIVATED_SOURCE });
                break;
            case FormState.Cancelation:
                shownCancel({ hhtmSource: CANCEL_SOURCE });
                break;
            case FormState.Error:
                shownError({ hhtmSource: ERROR_SOURCE });
                break;
        }
    }, [formState]);

    return (
        <>
            {!!orderCode && (
                <Alert
                    icon={<InfoCircleFilledSize24 />}
                    iconStyle="neutral"
                    onClose={() => null}
                    visible={formState === FormState.WaitingPayment}
                    layout="vertical"
                    title={trls[TrlKeys.waitingTitle]}
                    description={formatToReactComponent(trls[TrlKeys.waitingDescription], {
                        '{orderCode}': orderCode,
                    })}
                    buttons={
                        <Button mode="primary" loading size="medium" data-qa="hhpro-waiting-payment-loader">
                            {''}
                        </Button>
                    }
                />
            )}
            <Alert
                icon={<InfoCircleFilledSize24 />}
                iconStyle="neutral"
                onClose={setIdle}
                visible={formState === FormState.Prepayment}
                layout="vertical"
                title={trls[TrlKeys.prepaymentTitle]}
                description={trls[TrlKeys.prepaymentDescription]}
                buttons={
                    <Button mode="primary" size="medium" onClick={initPayment} data-qa="hhpro-prepayment-button">
                        {formatToReactComponent(trls[TrlKeys.prepaymentButton], {
                            '{price}': <FormatMoney currency={currency}>{price}</FormatMoney>,
                        })}
                    </Button>
                }
            />
            <Modal onClose={setIdle} visible={formState === FormState.Activated} size="small">
                {renderActivatedContent()}
                <VSpacing default={24} />
                {renderActivatedButton()}
            </Modal>
            <BottomSheet
                onClose={setIdle}
                visible={formState === FormState.Activated}
                data-qa="hhpro-activated-bottomsheet"
                footer={<BottomSheetFooter>{renderActivatedButton()}</BottomSheetFooter>}
            >
                {renderActivatedContent()}
            </BottomSheet>
            <Alert
                icon={<ExclamationTriangleFilledSize24 />}
                iconStyle="warning"
                onClose={setIdle}
                visible={formState === FormState.Error}
                layout="vertical"
                title={trls[TrlKeys.errorTitle]}
                description={trls[TrlKeys.errorDescription]}
                buttons={
                    <Button
                        mode="primary"
                        size="medium"
                        onClick={runLastAction}
                        loading={isFetching}
                        data-qa="hhpro-error-close-button"
                    >
                        {trls[TrlKeys.errorButton]}
                    </Button>
                }
            />
            {!!subscription && (
                <Alert
                    icon={<InfoCircleFilledSize24 />}
                    iconStyle="negative"
                    onClose={setIdle}
                    visible={formState === FormState.Cancelation}
                    layout="vertical"
                    title={trls[TrlKeys.cancel.title]}
                    description={formatToReactComponent(trls[TrlKeys.cancel.description], {
                        '{date}': <HumanDate date={subscription.expiredAt} />,
                    })}
                    buttons={
                        <>
                            <Button
                                mode="primary"
                                size="medium"
                                onClick={() => {
                                    clickCancelDecline({ tariffId: subscription.tariff.id, hhtmSource: CANCEL_SOURCE });
                                    setIdle();
                                }}
                                data-qa="hhpro-cancelation-decline"
                            >
                                {trls[TrlKeys.cancel.cancel]}
                            </Button>
                            <Button
                                mode="secondary"
                                style="negative"
                                size="medium"
                                onClick={() => {
                                    initCancel();
                                    clickCancelConfirm({ tariffId: subscription.tariff.id, hhtmSource: CANCEL_SOURCE });
                                }}
                                data-qa="hhpro-cancelation-confirm"
                                loading={formState === FormState.FetchingCancel}
                            >
                                {trls[TrlKeys.cancel.apply]}
                            </Button>
                        </>
                    }
                />
            )}
            {!!subscription && showSnackbar && (
                <Snackbar
                    actionLabel={trls[TrlKeys.cancel.renew]}
                    onAction={() => {
                        initRenew();
                        clickRenew({ tariffId: subscription.tariff.id, hhtmFromLabel: 'snackbar' });
                    }}
                    showClose
                    align="top"
                    removeOnUnmount={true}
                    autohideTime={5000}
                    data-qa="hhpro-after-cancel-snackbar"
                    onClose={() => {
                        setShowSnackbar(false);
                    }}
                    addon={<EmojiSadFilledSize24 initial="contrast" />}
                >
                    {formatToReactComponent(trls[TrlKeys.cancel.complete], {
                        '{date}': <HumanDate date={subscription.expiredAt} />,
                    })}
                </Snackbar>
            )}
            <Modal visible={formState === FormState.PaymentProgress}>
                <iframe className={styles.paymentFrame} src={frameSrc} />
            </Modal>
        </>
    );
};

export default translation(PaymentState);
